class VerticalMenuTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    if (!this.tabs.length) return;

    // Enhance collection links to tabs; without JavaScript they still navigate.
    const categories = this.querySelector('.mega-tabs__categories');
    const tablist = document.createElement('div');
    tablist.className = 'mega-tabs__tablist';
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', categories.getAttribute('aria-label'));
    tablist.setAttribute('aria-orientation', 'vertical');
    // Keep direct links outside the tablist while preserving the menu's visual order.
    Array.from(categories.children).forEach((item, index) => {
      item.style.order = index;
      if (item.hasAttribute('data-tab')) tablist.append(item);
    });
    categories.prepend(tablist);

    // Track recent mouse movement so a diagonal trip toward the products doesn't switch tabs.
    this.lastPoint = null;
    this.movingTowardPanel = false;
    categories.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (this.lastPoint) {
        const dx = event.clientX - this.lastPoint.x;
        const dy = Math.abs(event.clientY - this.lastPoint.y);
        this.movingTowardPanel = dx > 0 && dx >= dy * 0.5;
      }
      this.lastPoint = { x: event.clientX, y: event.clientY };
    });
    this.querySelector('.mega-tabs__panels')?.addEventListener('pointerenter', () => this.cancelHover());

    this.tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tab.dataset.tab);
      tab.addEventListener('pointerdown', (event) => {
        this.lastPointerType = event.pointerType;
      });
      tab.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.cancelHover();
        // Switch only once the pointer rests on the tab; wait longer while heading toward the products.
        const delay = this.movingTowardPanel ? 400 : 120;
        this.hoverTimer = setTimeout(() => this.select(index), delay);
      });
      tab.addEventListener('pointerleave', () => this.cancelHover());
      tab.addEventListener('click', (event) => {
        const pointerType = this.lastPointerType;
        this.lastPointerType = null;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        // Mouse and keyboard (Enter) follow the collection link; a touch tap first opens the tab, a second tap navigates.
        if (pointerType !== 'touch' && pointerType !== 'pen') return;
        if (tab.getAttribute('aria-selected') === 'true') return;
        event.preventDefault();
        this.select(index);
      });
      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (event.key === ' ') next = index;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      });
    });
    this.panels.forEach((panel) => panel.setAttribute('role', 'tabpanel'));
    this.select(0);
  }

  cancelHover() {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  select(index) {
    this.tabs.forEach((tab, current) => {
      tab.setAttribute('aria-selected', String(current === index));
      tab.tabIndex = current === index ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== this.tabs[index].dataset.tab;
    });
  }
}

if (!customElements.get('vertical-menu-tabs')) {
  customElements.define('vertical-menu-tabs', VerticalMenuTabs);
}
